import { ChildrenLabelMapper } from 'purespectrum-lib';
import {
  QUALIFICATION_CODES,
  CAG_NO_CHILDREN_TEXT,
} from '../../shared/constants/qualification-code';
import {
  Criteria,
  QuotaPayload,
} from '../../shared/interfaces/quota.interface';
import { QuotaNumberCount } from './quota-number-count';

export interface QuotaBuilder {
  build(): QuotaPayload;
}

export class CriteriaName {
  constructor(private readonly _criteria: Criteria) {}

  generate(): string {
    if (
      this._criteria &&
      this._criteria.range_sets &&
      this._criteria.range_sets.length
    ) {
      const rangeSet = this._criteria.range_sets[0];
      return `${rangeSet.from}-${rangeSet.to}`;
    }

    if (
      this._criteria.condition_names &&
      this._criteria.condition_names.length
    ) {
      return this._criteria.condition_names[0];
    } else if (this._criteria.group_name) {
      return this._criteria.group_name;
    }

    return '';
  }
}

export class SimpleQuotaBuilder implements QuotaBuilder {
  constructor(
    private readonly _counter: QuotaNumberCount,
    private readonly _criteria: Criteria
  ) {}

  build(): QuotaPayload {
    const number = this._counter.calculate(this._criteria.original_percentage!);
    const name = new CriteriaName(this._criteria).generate();

    const quota = {
      buyer_quota_id: '',
      maximum: this._criteria.original_maximum,
      minimum: this._criteria.original_minimum,
      percentage: this._criteria.original_percentage,
      quota_title: name,
      flexible: this._criteria.original_flex ?? { enable: true, value: 0 },
      selected: true,
      required_count: this._criteria.original_required_count ?? number,
      number: this._criteria.original_number ?? number,
      text: name,
      criteria: [this._criteria],
    } as QuotaPayload;

    if (
      this._criteria.qualification_code === QUALIFICATION_CODES.CHILD_AGE_GENDER
    ) {
      const mapper = new ChildrenLabelMapper(
        this._criteria.condition_codes!.map(Number)
      );
      quota.min = mapper.min();
      quota.max = mapper.max();
    }
    return quota;
  }
}

export class GroupQuotaBuilder implements QuotaBuilder {
  constructor(
    private readonly _counter: QuotaNumberCount,
    private readonly _criteria: Criteria
  ) {}

  build(): QuotaPayload {
    const quota = new SimpleQuotaBuilder(this._counter, this._criteria);
    return {
      ...quota.build(),
      selectedForGroup: true,
      oldQuotasText: this._criteria.condition_names,
    };
  }
}

export class ChildrenQuotaBuilder implements QuotaBuilder {
  constructor(
    private readonly _counter: QuotaNumberCount,
    private readonly _criteria: Criteria
  ) {}

  public build(): QuotaPayload {
    const quota = new SimpleQuotaBuilder(this._counter, this._criteria).build();
    const quota_text = quota.text === 'No Children' ? quota.text : 'Children';

    const children = {
      ...quota,
      quota_title: quota_text,
      text: this._text(quota.text),
      criteria: [
        {
          ...this._criteria,
          qualification_name: quota_text,
        },
      ],
    };

    this._age(children);

    const condition_codes = children.criteria[0].condition_codes;
    if (condition_codes) {
      children.gender = this._gender(condition_codes, children.criteria[0]);
    }

    return children;
  }

  private _age(quota: QuotaPayload) {
    const ranger = quota.criteria[0].range_sets;
    if (ranger && ranger[0].units) {
      quota.max = ranger[0].to;
      quota.min = ranger[0].from;
      quota.ageunit = ranger[0].units;
    }
  }

  private _gender(conditions: string[], criteria: Criteria): string {
    if (conditions.length > 1) {
      criteria.condition_names = ['both'];
      return 'both';
    }

    return conditions[0];
  }

  private _text(quotaText: string | undefined): string {
    if (quotaText === CAG_NO_CHILDREN_TEXT) {
      return 'No Children';
    }
    return quotaText === 'No Children' ? quotaText : 'Children';
  }
}

export class QuotaIdentifierBuilder implements QuotaBuilder {
  constructor(
    private readonly _quota: QuotaPayload,
    private readonly _quotaId: string
  ) {}

  public build(): QuotaPayload {
    return {
      ...this._quota,
      ...this._identifier(),
    };
  }

  private _identifier() {
    return {
      buyer_quota_id: this._quotaId,
      quota_text: undefined,
      quota_title: undefined,
    };
  }
}
